// src/components/circles/CirclePulse.jsx
//
// Everyone in the circle answers the same fixed-option question about
// themselves. Nothing shows until everyone asked has answered (or, with at
// least two answers in, three days have passed). Then every answer appears at
// once, with names, and the divergence is the conversation: "You picked that?
// Since when?"
//
// Three things it deliberately doesn't do:
//
//   - There is nowhere to type. Fixed options mean the worst thing anyone can
//     say here is "I picked B".
//   - It doesn't name who hasn't answered yet. It shows a count. Pointing a
//     small group at the one person holding things up is the pressure the
//     "every member" challenges put on people, and they're gone.
//   - It doesn't call itself anonymous, because it isn't: when it reveals,
//     everyone asked sees who picked what, and it says so before you answer.
//
// The rules (firestore.rules) are what actually keep answers hidden; this
// screen just mirrors them (utils/circlePulse.js).

import { useCallback, useEffect, useState } from 'react';
import { Check, Eye, HelpCircle, Lock } from 'lucide-react';
import friendCirclesService from '../../services/circles/FriendCirclesService';
import { pulseStatus } from '../../utils/circlePulse';

// "later today", "tomorrow", or the day itself ("Mon 5 Oct"), in the
// reader's own time zone and date format.
function whenLabel(ms, now) {
  const day = (t) => { const d = new Date(t); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); };
  const days = Math.round((day(ms) - day(now)) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'later today';
  if (days === 1) return 'tomorrow';
  return new Date(ms).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

const SAVE_FAILED = "That didn't save. Check your connection and try again.";

export default function CirclePulse({ circle, userId, memberNames = {}, onChanged, now }) {
  const [answers, setAnswers] = useState({});
  const [myChoice, setMyChoice] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const at = now ?? Date.now();
  const status = pulseStatus(circle, userId, at);
  const until = (ms) => {
    const w = whenLabel(ms, at);
    return w === 'later today' || w === 'tomorrow' ? w : `until ${w}`;
  };
  const { phase, question } = status;
  const circleId = circle?.id;
  const pulseId = status.pulseId;

  // Your own answer, so it can be shown as yours while it's hidden from
  // everyone else; and everyone's, once revealed.
  const loadAnswers = useCallback(async () => {
    if (!circleId || !pulseId) return;
    if (phase === 'revealed' && status.askedToMe) {
      const res = await friendCirclesService.getPulseAnswers(circle);
      if (res.success) {
        setAnswers(res.answers);
        setMyChoice(res.answers[userId] ?? null);
      }
    } else if (status.iHaveAnswered) {
      const res = await friendCirclesService.getMyPulseAnswer(circle, userId);
      if (res.success) setMyChoice(res.choice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circleId, pulseId, phase, status.askedToMe, status.iHaveAnswered, userId]);

  useEffect(() => {
    setAnswers({});
    setMyChoice(null);
    setPicked(null);
    loadAnswers();
  }, [loadAnswers]);

  // Reveal once it's due: everyone asked has answered, or three days are up
  // with two answers in. Whoever opens the circle first does it; the rules
  // check it really is due.
  useEffect(() => {
    if (!status.shouldReveal || !circleId) return;
    let cancelled = false;
    (async () => {
      const res = await friendCirclesService.revealPulse(circleId);
      if (!cancelled && res.success) onChanged?.();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.shouldReveal, circleId, pulseId]);

  const run = async (action) => {
    setBusy(true);
    setError('');
    const res = await action();
    setBusy(false);
    if (!res?.success) {
      setError(SAVE_FAILED);
      return false;
    }
    onChanged?.();
    return true;
  };

  const start = () => run(() => friendCirclesService.startPulse(circle));

  const lockIn = async () => {
    if (picked == null) return;
    const ok = await run(() => friendCirclesService.answerPulse(circle, userId, picked));
    if (ok) setMyChoice(picked);
  };

  const nameFor = (uid) => (uid === userId ? 'You' : memberNames[uid] || 'A friend');

  const header = (title, subtitle) => (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <HelpCircle aria-hidden="true" className="w-5 h-5 text-amber-300" />
      </div>
      <div className="min-w-0">
        <h2 id="circle-pulse-title" className="text-lg font-bold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-slate-300 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  const errorLine = error && (
    <p role="alert" className="mt-4 text-sm text-red-200 bg-red-500/10 border border-red-400/30 rounded-lg px-3 py-2">
      {error}
    </p>
  );

  const askButton = (label) => (
    <button
      type="button"
      onClick={start}
      disabled={busy}
      className="mt-4 w-full sm:w-auto px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-semibold disabled:opacity-50"
    >
      {label}
    </button>
  );

  const nextQuestionLine = () => {
    if (!status.enoughPeople) return null;
    if (status.canStart) return askButton('Ask the next question');
    return (
      <p className="mt-4 text-sm text-slate-400">
        Anyone in the circle can ask the next question {(() => {
          const w = whenLabel(status.nextStartAt, at);
          return w === 'later today' || w === 'tomorrow' ? w : `from ${w}`;
        })()}.
      </p>
    );
  };

  const shell = (children) => (
    <section
      aria-labelledby="circle-pulse-title"
      className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10"
    >
      {children}
    </section>
  );

  // ── No question yet ───────────────────────────────────────────────────────
  if (phase === 'none' || !question) {
    return shell(
      <>
        {header('Same question, all at once',
          'Everyone picks an answer from a few fixed options. Nobody sees anyone else’s until everyone has picked (or after three days, if at least two have). Then everyone sees who picked what, all at once. There’s nowhere to type anything.')}
        {status.enoughPeople
          ? askButton('Ask this week’s question')
          : (
            <p className="text-sm text-slate-400">
              Once someone else has joined, anyone in the circle can ask the first question.
            </p>
          )}
        {errorLine}
      </>
    );
  }

  // ── Revealed ──────────────────────────────────────────────────────────────
  if (phase === 'revealed') {
    if (!status.askedToMe) {
      return shell(
        <>
          {header(question.question, 'This one was asked before you joined, so its answers aren’t yours to see. You’ll be in the next one.')}
          {nextQuestionLine()}
          {errorLine}
        </>
      );
    }
    const pickedBy = question.options.map((_, i) =>
      Object.entries(answers).filter(([, c]) => c === i).map(([uid]) => uid));
    return shell(
      <>
        {header(question.question, 'Everyone’s answers')}
        <ul className="space-y-2">
          {question.options.map((option, i) => {
            const mine = myChoice === i;
            return (
              <li
                key={option}
                className={`px-4 py-3 rounded-lg border ${
                  mine ? 'bg-amber-500/15 border-amber-400/40' : 'bg-slate-900/40 border-white/10'
                }`}
              >
                <p className="text-slate-100 text-sm">{option}</p>
                {pickedBy[i].length > 0 && (
                  <p className="text-xs text-amber-200 mt-1 break-words">
                    {pickedBy[i].map(nameFor).join(', ')}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        <p className="mt-4 text-sm text-slate-300">
          Now ask each other why. That part happens in person, not in here.
        </p>
        {nextQuestionLine()}
        {errorLine}
      </>
    );
  }

  // ── Open ──────────────────────────────────────────────────────────────────
  const count = `${status.answeredCount} of ${status.askedCount} have answered`;
  const hiddenUntil = status.waitingForSecondAnswer
    ? 'It shows as soon as one more person answers.'
    : `Hidden until everyone has answered, or ${until(status.revealDueAt)} if two or more have by then.`;

  if (!status.askedToMe) {
    return shell(
      <>
        {header(question.question, 'This one was asked before you joined, so it isn’t yours to answer. You’ll be in the next one.')}
        <p className="text-sm text-slate-400">{count}.</p>
        {status.canStart && askButton('Ask a new question')}
        {errorLine}
      </>
    );
  }

  if (status.iHaveAnswered) {
    return shell(
      <>
        {header(question.question, count)}
        <ul className="space-y-2">
          {question.options.map((option, i) => (
            <li
              key={option}
              className={`px-4 py-3 rounded-lg border text-sm ${
                myChoice === i
                  ? 'bg-amber-500/15 border-amber-400/40 text-white'
                  : 'bg-slate-900/40 border-white/10 text-slate-400'
              }`}
            >
              {option}
              {myChoice === i && <span className="sr-only"> (your answer)</span>}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-teal-200 flex items-start gap-2">
          <Check aria-hidden="true" className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Locked in. {hiddenUntil}</span>
        </p>
        {status.canStart && askButton('Ask a new question')}
        {errorLine}
      </>
    );
  }

  return shell(
    <>
      {header(question.question, count)}
      <fieldset>
        <legend className="sr-only">Pick one answer</legend>
        <div className="space-y-2">
          {question.options.map((option, i) => {
            const selected = picked === i;
            return (
              <label
                key={option}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                  selected
                    ? 'bg-amber-500/15 border-amber-400/50 text-white'
                    : 'bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70'
                }`}
              >
                <input
                  type="radio"
                  name={`pulse-${pulseId}`}
                  value={i}
                  checked={selected}
                  onChange={() => setPicked(i)}
                  className="accent-amber-400 w-4 h-4 flex-shrink-0"
                />
                <span className="text-sm">{option}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <div className="mt-4 rounded-lg bg-slate-900/40 border border-white/10 p-3 text-sm text-slate-300 flex items-start gap-2">
        <Eye aria-hidden="true" className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-200" />
        <span>
          Nobody sees your answer until everyone has answered, or {until(status.revealDueAt)} if
          two or more have by then. Then everyone in the circle who was asked sees who picked what. It isn&apos;t
          anonymous, and you can&apos;t change it once it&apos;s locked in.
        </span>
      </div>
      <button
        type="button"
        onClick={lockIn}
        disabled={picked == null || busy}
        className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-semibold disabled:opacity-50"
      >
        <Lock aria-hidden="true" className="w-4 h-4" />
        Lock in my answer
      </button>
      {errorLine}
    </>
  );
}
